import { apiClient } from '@/shared/api/client';
import { API_ENDPOINTS } from '@/shared/constants/api/endpoints.ts';
import type {
  AnswerInquiryInput,
  CreateInquiryInput,
  Inquiry,
  InquiryListParams,
  Paginated,
  UpdateInquiryInput,
} from '../inquiry.type.ts';

/**
 * 1:1 문의 CRUD 호출부.
 *
 * 게시판(board)과 달리 전 경로 인증 필요 → skipAuthRedirect 를 쓰지 않는다.
 * (비로그인 시 401 → 인터셉터가 로그인으로 리다이렉트)
 * 목록은 본인 문의만(운영자=전체), 답변은 운영자만 — 인가는 서버가 최종 판단.
 */
export const inquiryApi = {
  getList: async (params: InquiryListParams): Promise<Paginated<Inquiry>> => {
    const { data } = await apiClient.get<Paginated<Inquiry>>(
      API_ENDPOINTS.inquiries.list,
      { params },
    );
    return data;
  },

  getDetail: async (id: string | number): Promise<Inquiry> => {
    const { data } = await apiClient.get<Inquiry>(
      API_ENDPOINTS.inquiries.detail(id),
    );
    return data;
  },

  markInquiryRead: async (
    id: string | number,
  ): Promise<void> => {
    await apiClient.post(
      API_ENDPOINTS.inquiries.markInquiryRead(id),
    );
  },

  getUserMenuNotification: async (): Promise<boolean> => {
    const { data } = await apiClient.get<boolean>(
      API_ENDPOINTS.inquiries.userMenuNotification,
    );
    return data;
  },

  create: async (input: CreateInquiryInput): Promise<Inquiry> => {
    const { data } = await apiClient.post<Inquiry>(
      API_ENDPOINTS.inquiries.create,
      input,
    );
    return data;
  },

  update: async (
    id: string | number,
    input: UpdateInquiryInput,
  ): Promise<Inquiry> => {
    const { data } = await apiClient.put<Inquiry>(
      API_ENDPOINTS.inquiries.update(id),
      input,
    );
    return data;
  },

  remove: async (id: string | number): Promise<void> => {
    await apiClient.delete(API_ENDPOINTS.inquiries.remove(id));
  },

  /** 운영자 답변 등록 (MANAGER+) */
  answer: async (
    id: string | number,
    input: AnswerInquiryInput,
  ): Promise<Inquiry> => {
    const { data } = await apiClient.post<Inquiry>(
      API_ENDPOINTS.inquiries.answer(id),
      input,
    );
    return data;
  },
};
